// Accessibility checks for a questionnaire, based on WCAG 2.2 guidance for
// forms: text alternatives, motion, contrast, and layouts that are hard to
// use with a screen reader, a keyboard or on a phone. Advice only; nothing
// here blocks publishing.
import { coreT, type CoreMessageKey, type Locale } from './i18n';
import type { Definition, Question } from './types';
import { isHexColor } from './types';

export interface AccessibilityIssue {
  questionId?: string;
  level: 'error' | 'warning';
  /** WCAG success criterion, e.g. "1.1.1". */
  wcag: string;
  message: string;
}

function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * c[0]! + 0.7152 * c[1]! + 0.0722 * c[2]!;
}

/** WCAG contrast ratio between two #rrggbb colors. */
export function contrastRatio(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p);
  return Math.round(((x! + 0.05) / (y! + 0.05)) * 100) / 100;
}

const VAGUE_LINK = /\b(click here|klik di sini|klik disini|tap here)\b/i;
const TEXT_COLOR = '#1f1f1f';
const CARD_COLOR = '#ffffff';

export function checkAccessibility(def: Definition, locale: Locale = 'en'): AccessibilityIssue[] {
  const out: AccessibilityIssue[] = [];
  const add = (level: AccessibilityIssue['level'], wcag: string, questionId: string | undefined, key: CoreMessageKey, ...args: (string | number)[]) =>
    out.push({ level, wcag, questionId, message: coreT(locale, key, ...args) });

  def.questions.forEach((q: Question, i) => {
    const n = i + 1;
    const m = q.media;
    if (m?.type === 'image' && !m.alt?.trim()) add('error', '1.1.1', q.id, 'a11y.image_alt', n);
    if (m?.type === 'video' && !m.alt?.trim()) add('warning', '1.2.1', q.id, 'a11y.video_caption', n);
    if (m?.type === 'video' && m.autoplay && !m.muted) add('error', '1.4.2', q.id, 'a11y.autoplay_sound', n);
    if (q.options?.some((o) => o.imageUrl && o.label.trim().length < 2)) add('error', '1.1.1', q.id, 'a11y.option_image_label', n);
    if (q.type === 'matrix' && (q.options?.length ?? 0) > 7) add('warning', '1.3.1', q.id, 'a11y.matrix_wide', n, q.options!.length);
    if (q.type === 'matrix' && (q.rows?.length ?? 0) > 12) add('warning', '2.4.6', q.id, 'a11y.matrix_long', n, q.rows!.length);
    if (q.type === 'ranking' && (q.options?.length ?? 0) > 10) add('warning', '2.1.1', q.id, 'a11y.ranking_long', n);
    if ((q.type === 'slider' || q.type === 'rating') && !q.scale?.minLabel && !q.scale?.maxLabel) add('warning', '3.3.2', q.id, 'a11y.scale_labels', n);
    if ((q.options?.length ?? 0) > 15 && q.type !== 'dropdown' && q.type !== 'matrix') add('warning', '2.4.6', q.id, 'a11y.many_options', n);
    if (VAGUE_LINK.test(`${q.title} ${q.description ?? ''} ${q.tooltip ?? ''}`)) add('warning', '2.4.4', q.id, 'a11y.vague_link', n);
    if (q.title.length > 20 && q.title === q.title.toUpperCase() && /[A-Z]/.test(q.title)) add('warning', '3.1.5', q.id, 'a11y.all_caps', n);
    if (q.type === 'signature' && q.required) add('warning', '2.5.1', q.id, 'a11y.signature_required', n);
    if (q.type === 'location' && q.required) add('warning', '3.3.2', q.id, 'a11y.location_required', n);
  });

  const visibleCount = def.questions.filter((q) => q.type !== 'descriptive').length;
  if ((def.settings.pageMode ?? 'question') === 'all' && visibleCount > 25) add('warning', '2.4.6', undefined, 'a11y.long_page', visibleCount);
  if (def.settings.showTimer) add('warning', '2.2.1', undefined, 'a11y.timer');

  const th = def.theme;
  if (th && isHexColor(th.primaryColor)) {
    const ratio = contrastRatio(th.primaryColor!, CARD_COLOR);
    if (ratio < 3) add('error', '1.4.11', undefined, 'a11y.accent_contrast', ratio);
  }
  if (th && isHexColor(th.backgroundColor)) {
    const ratio = contrastRatio(th.backgroundColor!, TEXT_COLOR);
    if (ratio < 4.5) add('error', '1.4.3', undefined, 'a11y.background_contrast', ratio);
  }
  return out;
}
